import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getQuizScores, getLearnedWords, getStudyStreak, getStoredFailedQuestions } from '../utils/storage';
import BackButton from '../components/BackButton';
import Icon from '../components/Icon';
import './styles/ProgressScreen.css';

const StatCard = ({ icon, title, value, color }) => (
  <div className="stat-card">
    <span className="stat-icon"><Icon name={icon} size={24} color={color || 'currentColor'} /></span>
    <span className="stat-value" style={color ? { color } : {}}>{value}</span>
    <span className="stat-title">{title}</span>
  </div>
);

const ScoreBar = ({ score, label, date }) => {
  const barColor = score >= 70 ? 'var(--color-success)' : score >= 40 ? 'var(--color-progress)' : 'var(--color-error)';
  return (
    <div className="score-bar-row">
      <div className="score-bar-info">
        <span className="score-bar-label">{label}</span>
        <span className="score-bar-date">{date}</span>
      </div>
      <div className="score-bar-track">
        <div
          className="score-bar-fill"
          style={{ width: `${Math.max(score, 5)}%`, backgroundColor: barColor }}
        />
      </div>
      <span className="score-bar-percent" style={{ color: barColor }}>{score}%</span>
    </div>
  );
};

const ProgressScreen = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [streak, setStreak] = useState({ count: 0, lastDate: null });
  const [learnedCount, setLearnedCount] = useState(0);
  const [quizScores, setQuizScores] = useState([]);
  const [failedCount, setFailedCount] = useState(0);

  useEffect(() => {
    setStreak(getStudyStreak());
    setLearnedCount(getLearnedWords().length);
    setQuizScores(getQuizScores());
    setFailedCount(getStoredFailedQuestions().length);
  }, []);

  const averageScore =
    quizScores.length > 0
      ? Math.round(quizScores.reduce((sum, s) => sum + s.percentage, 0) / quizScores.length)
      : 0;

  const recentScores = quizScores.slice(-10).reverse();

  return (
    <div className="progress-container">
      <BackButton to="/" label={t('home')} />
      <h2 className="progress-title">{t('progress')}</h2>

      <div className="stats-grid">
        <StatCard icon="flame" title={t('dayStreak')} value={streak.count} color="var(--color-progress)" />
        <div className="stat-card stat-card-clickable" onClick={() => navigate('/learned-words')}>
          <span className="stat-icon"><Icon name="book" size={24} color="var(--color-vocab)" /></span>
          <span className="stat-value" style={{ color: 'var(--color-vocab)' }}>{learnedCount}</span>
          <span className="stat-title">{t('wordsLearned')}</span>
          <span className="stat-link-hint">{t('viewAll')} &rsaquo;</span>
        </div>
        <StatCard icon="target" title={t('quizzesTaken')} value={quizScores.length} color="var(--color-writing)" />
        <StatCard icon="star" title={t('avgScore')} value={`${averageScore}%`} color={averageScore >= 70 ? 'var(--color-success)' : 'var(--color-error)'} />
      </div>

      {failedCount > 0 && (
        <button
          className="warning-card"
          onClick={() => navigate('/vocabulary-test-screen', { state: { totalQuestions: failedCount, selectedCategories: ['failed'] } })}
        >
          <div className="warning-header">
            <span className="warning-icon"><Icon name="warning" size={18} color="var(--color-warning)" /></span>
            <span className="warning-title">{t('wordsToReview')}</span>
          </div>
          <p className="warning-value">
            {t('wordsNeedPractice', { count: failedCount })}
          </p>
          <span className="warning-cta">{t('practiceNow')} &rsaquo;</span>
        </button>
      )}

      {recentScores.length > 0 && (
        <>
          <h3 className="section-title">{t('recentQuizScores')}</h3>
          <div className="chart-card">
            {recentScores.map((score, index) => (
              <ScoreBar
                key={index}
                score={score.percentage}
                label={score.quizType || t('vocabulary')}
                date={new Date(score.date).toLocaleDateString()}
              />
            ))}
          </div>
        </>
      )}

      {quizScores.length === 0 && learnedCount === 0 && (
        <div className="empty-state">
          <span className="empty-icon"><Icon name="bulb" size={48} color="var(--text-muted)" /></span>
          <p className="empty-title">{t('noProgressYet')}</p>
          <p className="empty-text">{t('startLearning')}</p>
        </div>
      )}
    </div>
  );
};

export default ProgressScreen;
